import { useState } from 'react';
import { Button, Drawer, Input, Segmented, useToast } from '@hana/ui';
import { useT } from '@hana/i18n';
import { IconPlayerPlay, IconTrash } from '@tabler/icons-react';
import { FEED_KIND, feedFields, feedMissing, type FeedFormKey } from './news';
import type { FeedSource } from './types';
import './FeedSourceDrawer.i18n';
import s from './news.module.css';


/** Ngăn kéo Thêm / Sửa nguồn feed (board Mint-MarketNews, `dw.isSrc`). Chạy thử / dò / lưu / xoá chờ bật ghi + worker market-feed. */
export function FeedSourceDrawer({ open, source, onClose }: { open: boolean; source: FeedSource | null; onClose: () => void }) {
  const t = useT();
  const toast = useToast();
  const isNew = !source;
  const [kind, setKind] = useState<FeedSource['kind']>(source?.kind ?? 'rss');
  const [vals, setVals] = useState<Partial<Record<FeedFormKey, string>>>(() => ({ name: source?.name, url: source?.url, match: source?.match, tag: source?.tag }));
  const [on, setOn] = useState(source?.enabled ?? false);
  const [tried, setTried] = useState(false);
  const [out, setOut] = useState<{ text: string; err?: boolean }[]>([]);
  const fields = feedFields(kind, isNew);
  const missing = feedMissing(kind, isNew, vals);
  const pending = () => toast({ msg: t('fd.noWrite'), tone: 'err' });
  const test = () => {
    if (kind !== 'index_news' && !(vals.url ?? '').trim()) setOut([{ text: t('fd.noUrl'), err: true }]);
    else setOut([{ text: t('fd.noTest') }]);
  };
  const save = () => {
    if (missing.length) { setTried(true); return; }
    pending();
  };

  return (
    <Drawer
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title={isNew ? t('fd.add') : t('fd.edit', { name: source.name })}
      description={isNew ? t('fd.newSub') : `${FEED_KIND[source.kind]} · ${source.id}`}
      footer={
        <div className={s.dwFoot}>
          {!isNew && <Button variant="danger" icon={<IconTrash size={16} aria-hidden="true" />} onClick={pending}>{t('fd.del')}</Button>}
          <span className={s.dwGrow} />
          <Button variant="primary" onClick={save}>{isNew ? t('fd.create') : t('fd.save')}</Button>
        </div>
      }
    >
      <div className={s.dwBody}>
        <div className={s.dwFields}>
          {fields.map((f) => (
            <div key={f.key} className={f.wide ? s.dwWide : undefined}>
              <Input
                label={f.label}
                required={f.req}
                placeholder={f.ph}
                hint={f.help}
                error={tried && missing.includes(f.key) ? t('fd.fill') : undefined}
                value={vals[f.key] ?? ''}
                onChange={(e) => setVals((o) => ({ ...o, [f.key]: e.target.value }))}
              />
            </div>
          ))}
        </div>
        <div className={s.dwKind}>
          <b>{t('fd.kind')}</b>
          <Segmented kind="toggle" label={t('fd.kindAria')} value={kind} onChange={(k) => isNew && setKind(k)} items={(Object.keys(FEED_KIND) as FeedSource['kind'][]).map((k) => ({ key: k, label: FEED_KIND[k] }))} />
        </div>
        <label className={s.dwOn}>
          <input type="checkbox" checked={on} onChange={() => setOn(!on)} />
          {t('fd.on')}
        </label>
        <section className={s.dwTest} aria-label={t('fd.testTitle')}>
          <div className={s.dwTestHead}>
            <b>{t('fd.testTitle')}</b>
            <Button size="sm" icon={<IconPlayerPlay size={16} aria-hidden="true" />} onClick={test}>{t('fd.run')}</Button>
            {kind === 'carrier' && <Button size="sm" variant="soft" onClick={() => toast({ msg: t('fd.noProbe'), tone: 'err' })}>{t('fd.probe')}</Button>}
          </div>
          {(out.length ? out : [{ text: t('fd.idle') }]).map((o, i) => (
            <div key={i} className={o.err ? s.down : s.sub}>{o.text}</div>
          ))}
        </section>
      </div>
    </Drawer>
  );
}
